import axios from 'axios';
import { Logger } from 'sdk/logger';
import ApiProvider from 'sdk/base/api-provider';
import { HTTP_HEADERS } from 'sdk/base/api-provider/constants';
import { generateUUID } from 'sdk/base/utilities';
import { appendQueryParam, isSuccessStatusCode } from './utility';
import { isEmpty } from 'lodash';
import globalSpinner from 'sdk/connector/globalSpinner';
import { getInstance } from 'sdk';

export class ApiHandler {
    /**
     * Makes an API call using the provided ApiProvider instance.
     *
     * @param {ApiProvider} api - The ApiProvider instance containing the API details.
     * @param feedbackHandler - The optional error feedback handler.
     * @returns {Promise<any>} - A promise that resolves to the response data, or null if the call fails.
     *
     * @throws {Error} - Throws an error if the API call fails.
     *
     * @example
     * const apiProvider = new ApiProvider({
     *   apiInterface: {
     *     method: 'GET',
     *     endpoint: '/example-endpoint',
     *     headers: { 'Custom-Header': 'value' },
     *     body: { key: 'value' }
     *   },
     *   options: {
     *     skipSpinner: true,
     *     skipError: false
     *   },
     *   operationName: 'exampleOperation'
     * });
     * const response = await ApiHandler.call(apiProvider);
     * console.log(response);
     */
    static async call(api: ApiProvider) {
        const { 
            getBaseURL, 
            updateSessionAuth, 
            getSessionAuth, 
            dispatchAction, 
            getOptions, 
            getAuthInfo,
            updateSessionId
        } = getInstance().globalConfigApi;
        const { options, apiInterface, operationName } = api;
        const { skipSpinner, skipError } = options || {};
        const { url } = getBaseURL() || {};
        let response = null;
        let callUUID;
        try {
            dispatchAction('CLEAN_ERROR_STATE');
            const authInfo = getAuthInfo();
            const addBaseHeaders = !apiInterface.headers || isEmpty(apiInterface.headers);
            const requestHeaders = (!apiInterface.headers || addBaseHeaders) ? {
                [HTTP_HEADERS.CONTENT_TYPE]: 'application/json',
                [HTTP_HEADERS.ACCEPT]: 'application/json',
                [HTTP_HEADERS.LOCALE]: 'en/US',
                ...apiInterface.additionalHeaders
            } : apiInterface.headers;
            if ((authInfo?.serverId || authInfo?.tenantId) && addBaseHeaders) {
                requestHeaders[HTTP_HEADERS.SERVER_ID] = authInfo.serverId;
                requestHeaders[HTTP_HEADERS.TENANT_ID] = authInfo.tenantId;
            }
            let endpoint = apiInterface.endpoint;
            const options = getOptions();
            const auth = getSessionAuth();
            if (options.auth?.in === 'QUERY' && endpoint && options.auth?.key) {
                endpoint = appendQueryParam(endpoint, options.auth?.key, auth as string);
            } else if (addBaseHeaders) {
                requestHeaders[HTTP_HEADERS.AUTHORIZATION] = auth as string;
            }
            if (!skipSpinner) {
                callUUID = globalSpinner.add(operationName || apiInterface.endpoint || generateUUID());
            }
            response = await axios({
                method: apiInterface.method,
                baseURL: url,
                url: endpoint,
                headers: requestHeaders,
                data: apiInterface.body
            });
            // Update *** 27 Nov 2024 ***
            //      - Kenan: There is no Authorization header in response headers 
            if (response.headers[HTTP_HEADERS.AUTHORIZATION]) {
                updateSessionAuth(response.headers[HTTP_HEADERS.AUTHORIZATION]);
            }
            if (response.headers[HTTP_HEADERS.SESSION_ID]) {
                updateSessionId(response.headers[HTTP_HEADERS.SESSION_ID]);
            }
            if (!isSuccessStatusCode(response.status)) {
                return null;
            }
        } catch (err: any) {
            Logger.log(`Failed to Fetch query with error code: ${response?.status || '(failed)'}`, err, 'error');
            if (!skipError) {
                Logger.log('skipping error', `Skip Error for below exception ${err}`, 'warn');
            }
            return null;
        } finally {
            globalSpinner.remove(callUUID);
        }
        return response.data;
    }
}

export default ApiHandler;
